import { Box, Tab, Tabs } from '@mui/material';
import { connect } from 'react-redux';
import {
  DataGridPro,
  gridPaginatedVisibleSortedGridRowIdsSelector,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { get, sortBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { openGenericModal } from '@actions/modal';
import { showErrorMessage, showMessage } from '@actions/messageconfirmation';

/** SERVICES */
import productionPlanningService from '@services/productionPlanning';

/** COMMONS */
import { Button, Dropdown } from '@commons/utils/styledLibraryComponents';
import { DATAGRID_LOCALES, DATAGRID_PAGE_SIZE_OPTIONS } from '@commons/utils/dataGrid';
import { DataGridLoadingOverlay } from '@commons/DataGridLoadingOverlay';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import {
  GenericContentContainer,
  GenericGapContainer,
  GenericPageContainer,
} from '@commons/Layout/styledComponents';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getTimeSlotsToUse } from '../utils/commons';
import { getUserTimezone } from '@commons/utils/date';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { TabPanel } from '@commons/TabPanel';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

/** SELECTORS */
import {
  canEditProductionPlanningV2Forecasts,
  canEditProductionPlanningV2StocksProduced,
} from '@selectors/actions/productionActions';
import { getAuthorizedActions, getPrevHorizon } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

/** UTILS */
import { exportProductionPlanning } from './utils/export';
import { getActions } from './utils/actions';
import { getEntitiesColumns } from './utils/columns/getEntitiesColumns';
import { getProductsColumns } from './utils/columns/getProductsColumns';
import { getResetWarningModalParams } from './utils/modals';
import { ProductionPlanningDatagridToolbar } from './utils/ProductionPlanningDatagridToolbar';
import { StyledCheckbox, StyledDataGridContainer } from './styledComponents';

import {
  COLUMN_TYPES,
  DATE_CHANGE,
} from '../../forecast/Forecast/SalesPlanning/datagrid/constants';
import {
  COMMON_DATAGRID_PROPS,
  DATAGRID_SLOTS,
} from '../../forecast/Forecast/SalesPlanning/datagrid/datagridProps';
import { convertInpulseUnitToEntityQuantity } from '@commons/utils/conversion';
import { getRecipesColumns, RECIPE_UNIT_TYPE } from './utils/columns/getRecipesColumns';
import { roundWithDecimals } from '@commons/utils/math';

const keyToUse = {
  inStock: 'inStock',
  produced: 'produced',
  forecast: 'operatorForecast',
};

/**
 * Unique key of a date and store data set, used in the rows ids to force the DataGrid to re-render the rows
 */
const getDataKey = (date, storeId) =>
  `${moment(date).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY)}-${storeId}`;

const formatRecipeRows = (recipes, dataKey) =>
  Object.values(recipes).map((recipe) => ({
    id: `${recipe.id}-${dataKey}`, // Make ID unique to force re-render
    originalRecipeId: recipe.id, // Keep original ID for API calls
    name: recipe.name,
    img: recipe.img,
    category: get(recipe, 'category', null) || i18next.t('GENERAL.NONE_VALUE'),
    dataByTimeSlotIds: recipe.dataByTimeSlotIds,
    totalToProduce: recipe.totalToProduce,
    unit: recipe.unit,
    batchProduction: recipe.batchProduction,
    isIngredient: false,
    unitQuantity: recipe.unitQuantity,
  }));

const formatIngredientRows = (ingredients) =>
  Object.values(ingredients).map((ingredient) => ({
    id: ingredient.id,
    name: ingredient.name,
    img: ingredient.img,
    category: ingredient.category,
    dataByTimeSlotIds: ingredient.dataByTimeSlotIds,
    totalToProduce: ingredient.totalToProduce,
    unit: ingredient.unit,
    isIngredient: true,
  }));

const ProductionPlanning = (props) => {
  const {
    match,
    prevHorizon,
    stores,
    user,
    showErrorMessage,
    authorizedActions,
    client: { storeName },
    openGenericModal,
  } = props;

  const clientStoreName = getClientStoreNameTranslation(storeName, false);

  const USER_TIMEZONE = getUserTimezone();
  const TODAY = moment.tz(USER_TIMEZONE);

  /*******************
   ***** STATES *****
   *******************/

  const [isLoading, setIsLoading] = useState(false);
  const [actions, setActions] = useState();
  const [selectedRows, setSelectedRows] = useState([]);

  // Using ref to avoid stale closure issue occurring with the fill production action
  const selectedRowsRef = useRef(selectedRows);

  // Datagrid selected date
  const [selectedDate, setSelectedDate] = useState(TODAY);

  // Tabs management
  const [tabValue, setTabValue] = useState(0);

  // Header
  const [selectedStore, setSelectedStore] = useState(stores[0]);

  // Time slots
  const [timeSlots, setTimeSlots] = useState([]);

  // Datagrid Columns
  const [productsColumns, setProductsColumns] = useState([]);
  const [recipesColumns, setRecipesColumns] = useState([]);
  const [ingredientsColumns, setIngredientsColumns] = useState([]);

  // Datagrid pagination - separate for each datagrid for independent pagination
  const [productsPaginationModel, setProductsPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [recipesPaginationModel, setRecipesPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [ingredientsPaginationModel, setIngredientsPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  /**
   * Each tab will have its own data, rows and columns (although columns are identical between recipes and ingredients)
   */
  const [productsData, setProductsData] = useState({ rows: [], raw: [] });
  const [recipesData, setRecipesData] = useState({ rows: [], raw: [] });
  const [ingredientsData, setIngredientsData] = useState([]);

  // Focus state for input buttons
  const [focusedInput, setFocusedInput] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  // Recipe unit selections - tracks which unit (base or batch) is selected per recipe
  // Key: originalRecipeId, Value: 'base' | 'batch'
  const [recipeUnitSelections, setRecipeUnitSelections] = useState({});

  // Incremented on each production entries fetch (full fetch or post-save refresh): only the latest one is displayed
  const latestFetchIdRef = useRef(0);

  // Id of the latest full fetch: a full fetch replaced by another one is dropped instead of being fetched again
  const latestFullFetchIdRef = useRef(0);

  // Incremented after each successful save: a full fetch overtaken by a save is fetched again
  const completedSavesCountRef = useRef(0);

  // Date and store of the displayed planning: a response fetched for another selection is ignored
  const displayedDataKeyRef = useRef(null);

  // DataGrid API ref for controlling column order
  const apiRef = useGridApiRef();

  const enableReset = useMemo(() => {
    const isInPast = moment(selectedDate).isBefore(moment(), 'day');

    if (selectedRows.length) {
      return !isInPast && selectedRows.some((product) => product.hasOperatorForecast);
    }

    return !isInPast && productsData.rows.some((product) => product.hasOperatorForecast);
  }, [productsData, selectedRows]);

  /*******************
   ***** USEEFFECTS *****
   *******************/

  // Handle clicks outside inputs to remove focus
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside any input or button
      const isInputClick = event.target.closest('[data-input-container]');
      const isButtonClick =
        event.target.closest('[data-increment-button]') ||
        event.target.closest('[data-decrement-button]');

      if (!isInputClick && !isButtonClick) {
        setFocusedInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate columns when store changes and has timeslots
  useEffect(() => {
    // Get sorted and filtered rows to handle keyboard navigation between inputs
    const allSortedRows = apiRef.current?.getSortedRows() || [];
    const visibleRowIds = new Set(gridPaginatedVisibleSortedGridRowIdsSelector(apiRef) || []);

    const sortedVisibleRows = allSortedRows.filter((row) => visibleRowIds.has(row.id));

    const canEditForecasts = canEditProductionPlanningV2Forecasts(authorizedActions);
    const canEditStocksProduced = canEditProductionPlanningV2StocksProduced(authorizedActions);

    setProductsColumns(
      getProductsColumns({
        timeSlots,
        onStockChange: (id, timeSlotId, value) =>
          handleProductChange(id, timeSlotId, 'inStock', value),
        onProducedChange: (id, timeSlotId, value) =>
          handleProductChange(id, timeSlotId, 'produced', value),
        onForecastChange: (id, timeSlotId, value) =>
          handleProductChange(id, timeSlotId, 'forecast', value),
        focusedInput,
        onFocusChange: setFocusedInput,
        originalProductsData: productsData.raw,
        formattedProductsData: productsData.rows,
        sortedVisibleRows,
        isForecastDisabled: !canEditForecasts || moment(selectedDate).isBefore(TODAY, 'day'),
        isStockOrProducedDisabled: !canEditStocksProduced,
        onFillTimeSlotProduction: handleFillProduction,
        onClearTimeSlotQuantities: handleClearQuantities,
      }),
    );
    setRecipesColumns(
      getRecipesColumns({
        timeSlots,
        onStockChange: (id, timeSlotId, value) => {
          handleRecipeStockChange(id, timeSlotId, value);
        },
        focusedInput,
        onFocusChange: setFocusedInput,
        originalRecipesData: recipesData.raw,
        formattedRecipesData: recipesData.rows,
        sortedVisibleRows,
        recipeUnitSelections,
        onUnitChange: handleRecipeUnitChange,
      }),
    );
    setIngredientsColumns(getEntitiesColumns(COLUMN_TYPES.INGREDIENTS, timeSlots));
  }, [productsData, focusedInput, isLoading, timeSlots, authorizedActions, recipeUnitSelections]);

  useEffect(() => {
    if (!selectedStore || !selectedDate) {
      return;
    }

    getProductionEntries();
  }, [selectedDate, selectedStore]);

  const handleXLSExport = useCallback(() => {
    const selectedRows = apiRef.current.getSelectedRows();

    let productsToExport = productsData.rows;

    if (selectedRows.size > 0) {
      // getSelectedRows returns a Map, so we need to convert it to an array
      productsToExport = Array.from(selectedRows.values());
    }

    exportProductionPlanning(
      productsToExport,
      recipesData.rows,
      ingredientsData,
      timeSlots,
      selectedStore.name,
      selectedDate,
      clientStoreName,
    );
  }, [productsData, recipesData, ingredientsData, timeSlots, selectedStore, selectedDate]);

  const handleRefreshActionClick = useCallback(() => {
    const productsToReset = getSelectedProductsOrAllProducts(selectedRows);

    const productIdsToBeReset = productsToReset.map((product) => product.originalProductId);

    openGenericModal(
      getResetWarningModalParams({
        handleReset: () => {
          handleResetProductionEntries({ productIds: productIdsToBeReset });
        },
        formattedDate: moment(selectedDate).format(
          DATE_DISPLAY_FORMATS.CONDENSED_DAY_DATE_MONTH_YEAR,
        ),
      }),
    );
  }, [selectedRows, productsData, selectedDate, openGenericModal]);

  /**
   * Fill or clear production for selected products (or all if none selected)
   * @param {string|null} timeSlotId - If provided, only fill/clear for this specific time slot
   * @param {boolean} resetProduced - If true, clear quantities; if false, fill production
   */
  const handleFillOrClearProduction = useCallback(
    async (timeSlotId = null, resetProduced = false) => {
      setIsLoading(true);

      try {
        // Use ref to get current selectedRows (avoids stale closure issue)
        const currentSelectedRows = selectedRowsRef.current;
        const productsToUpdate = currentSelectedRows.length
          ? currentSelectedRows
          : productsData.rows;

        const productIds = productsToUpdate.map((product) => product.originalProductId);

        await productionPlanningService.fillProductionEntries(
          selectedStore.id,
          moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          productIds,
          {
            productionTimeSlotId: timeSlotId,
            resetProduced,
          },
        );

        await refetchAfterAction();
      } catch {
        const errorKey = resetProduced
          ? 'PRODUCTION.PLANNING.CLEAR_QUANTITIES_ERROR'
          : 'PRODUCTION.PLANNING.FILL_PRODUCTION_ERROR';
        showErrorMessage(i18next.t(errorKey));
      } finally {
        setIsLoading(false);
      }
    },
    [productsData.rows, selectedStore, selectedDate],
  );

  const handleFillProduction = useCallback(
    (timeSlotId = null) => handleFillOrClearProduction(timeSlotId, false),
    [handleFillOrClearProduction],
  );

  const handleClearQuantities = useCallback(
    (timeSlotId = null) => handleFillOrClearProduction(timeSlotId, true),
    [handleFillOrClearProduction],
  );

  useEffect(() => {
    setActions(
      getActions(
        handleXLSExport,
        handleRefreshActionClick,
        handleFillProduction,
        handleClearQuantities,
        {
          enableReset,
        },
      ),
    );
  }, [
    handleXLSExport,
    handleRefreshActionClick,
    handleFillProduction,
    handleClearQuantities,
    enableReset,
  ]);

  /*******************
   ***** METHODS *****
   *******************/
  // Helper to check if first timeslot forecast is focused
  const isFirstForecastFocused = () => {
    if (!focusedInput) {
      return false;
    }

    const firstTimeSlotId = sortBy(timeSlots, 'startTime')[0]?.id;

    return focusedInput.includes(`-${firstTimeSlotId}-forecast`);
  };

  const getProductionEntries = async () => {
    latestFetchIdRef.current += 1;

    const fetchId = latestFetchIdRef.current;
    const dataKey = getDataKey(selectedDate, selectedStore.id);
    const completedSavesCount = completedSavesCountRef.current;

    latestFullFetchIdRef.current = fetchId;
    displayedDataKeyRef.current = dataKey;

    // False once another date, store or full fetch replaced this one
    const isInChargeOfDisplay = () =>
      fetchId === latestFullFetchIdRef.current && dataKey === displayedDataKeyRef.current;

    setIsLoading(true);

    try {
      const { products, recipes, ingredients } =
        await productionPlanningService.getProductionEntries(
          selectedStore.id,
          moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        );

      if (!isInChargeOfDisplay()) {
        return;
      }

      // A save completed during this fetch (produced quantity included) may be missing from its response:
      // fetch again rather than display it
      if (completedSavesCount !== completedSavesCountRef.current) {
        await getProductionEntries();

        return;
      }

      // Process products data for datagrid
      const { formattedProducts, productTimeSlotIds } = Object.values(products).reduce(
        (acc, product) => {
          Object.keys(product.dataByTimeSlotIds).forEach((timeSlotId) => {
            acc.productTimeSlotIds.add(timeSlotId);
          });

          acc.formattedProducts.push({
            id: `${product.id}-${dataKey}`, // Make ID unique to force re-render
            originalProductId: product.id, // Keep original ID for API calls
            name: product.name,
            sku: product.sku,
            img: product.img,
            category: get(product, 'category', null) || i18next.t('GENERAL.NONE_VALUE'),
            dataByTimeSlotIds: product.dataByTimeSlotIds,
            totalToProduce: product.totalToProduce,
            producedTotal: product.producedTotal,
            hasOperatorForecast: product.hasOperatorForecast,
          });

          return acc;
        },
        { formattedProducts: [], productTimeSlotIds: new Set() },
      );

      const formattedRecipes = formatRecipeRows(recipes, dataKey);

      const initialRecipeUnitSelections = Object.fromEntries(
        formattedRecipes.map((recipe) => [
          recipe.originalRecipeId,
          recipe.batchProduction ? RECIPE_UNIT_TYPE.BATCH_PRODUCTION : RECIPE_UNIT_TYPE.BASE_UNIT,
        ]),
      );
      setRecipeUnitSelections(initialRecipeUnitSelections);

      setProductsData({ rows: formattedProducts, raw: products });
      setRecipesData({ rows: formattedRecipes, raw: recipes });
      setIngredientsData(formatIngredientRows(ingredients));

      const timeSlotsToUse = getTimeSlotsToUse(productTimeSlotIds, {
        selectedDate,
        selectedStore,
      });

      setTimeSlots(timeSlotsToUse);
    } catch {
      if (!isInChargeOfDisplay()) {
        return;
      }

      // The recipes refreshed by a save completed meanwhile must not be wiped: fetch again instead
      if (completedSavesCount !== completedSavesCountRef.current) {
        await getProductionEntries();

        return;
      }

      setProductsData({ rows: [], raw: [] });
      setRecipesData({ rows: [], raw: [] });
      setIngredientsData([]);

      showErrorMessage(i18next.t('PRODUCTION.PLANNING.FETCH_ERROR'));
    } finally {
      if (isInChargeOfDisplay()) {
        setIsLoading(false);
      }
    }
  };

  /**
   * Refetches the planning after an action (fill, clear, reset) made on the displayed date and store.
   * If another date or store got selected in the meantime, its own fetch is in charge of the display.
   */
  const refetchAfterAction = async () => {
    if (getDataKey(selectedDate, selectedStore.id) !== displayedDataKeyRef.current) {
      return;
    }

    await getProductionEntries();
  };

  /**
   * Recipes and ingredients needs depend on the whole composition tree (sub-recipe stocks, recipes reached
   * through several parents): the API computes them, so they are refetched after each change instead of being
   * patched locally. Products rows, time slots and recipe unit selections are left untouched.
   */
  const refreshRecipesAndIngredients = async () => {
    const dataKey = getDataKey(selectedDate, selectedStore.id);

    // The save was made on a date or store that is no longer displayed
    if (dataKey !== displayedDataKeyRef.current) {
      return;
    }

    latestFetchIdRef.current += 1;

    const fetchId = latestFetchIdRef.current;

    try {
      const { recipes, ingredients } = await productionPlanningService.getProductionEntries(
        selectedStore.id,
        moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      );

      // A more recent fetch was triggered, or another date or store was selected, in the meantime
      const isOutdated =
        fetchId !== latestFetchIdRef.current || dataKey !== displayedDataKeyRef.current;

      if (isOutdated) {
        return;
      }

      setRecipesData({ rows: formatRecipeRows(recipes, dataKey), raw: recipes });
      setIngredientsData(formatIngredientRows(ingredients));
    } catch {
      if (dataKey !== displayedDataKeyRef.current) {
        return;
      }

      showErrorMessage(i18next.t('PRODUCTION.PLANNING.FETCH_ERROR'));
    }
  };

  /*******************
   ***** HANDLERS *****
   *******************/

  const handleRecipeUnitChange = useCallback((recipeId, unitType) => {
    setRecipeUnitSelections((prev) => ({
      ...prev,
      [recipeId]: unitType,
    }));
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateChange = (direction) => {
    const newDate = new Date(selectedDate);

    if (direction === DATE_CHANGE.PREV) {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }

    setSelectedDate(newDate);
  };

  const handleProductChange = async (productId, timeSlotId, key, value) => {
    const productRows = [...productsData.rows];

    const hasValueChanged = productRows.some((product) => {
      if (product.originalProductId === productId) {
        return (
          /*
            When the time slot ID is missing from the product's time slot data, we treat this as a value change,
            which may occur during past data updates
          */
          !product.dataByTimeSlotIds[timeSlotId] ||
          product.dataByTimeSlotIds[timeSlotId][key] !== value
        );
      }

      return false;
    });

    if (!hasValueChanged) {
      return;
    }

    await handleSaveProduct(productId, timeSlotId, key, value);
  };

  const getUpdatedProductData = (previousProductsData, productId, timeSlotId, key, value) => {
    const updatedRows = previousProductsData.rows.map((product) => {
      // Use originalProductId for comparison since id now includes dataKey
      if (product.originalProductId === productId) {
        return {
          ...product,
          ...(keyToUse[key] === 'operatorForecast' ? { hasOperatorForecast: true } : {}), // in order to enable reset
          dataByTimeSlotIds: {
            ...product.dataByTimeSlotIds,
            [timeSlotId]: {
              ...product.dataByTimeSlotIds[timeSlotId],
              [key]: value,
            },
          },
        };
      }
      return product;
    });

    return { ...previousProductsData, rows: updatedRows };
  };

  const handleSaveProduct = async (productId, timeSlotId, key, value) => {
    setIsLoading(true);

    try {
      await productionPlanningService.postProductionEntry(
        selectedStore.id,
        moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        productId,
        timeSlotId,
        { [keyToUse[key]]: value },
      );

      completedSavesCountRef.current += 1;

      // The rows of another date or store must not be patched
      if (getDataKey(selectedDate, selectedStore.id) !== displayedDataKeyRef.current) {
        return;
      }

      // Patch the latest rows: a full fetch or another save may have updated them during this save
      setProductsData((previousProductsData) =>
        getUpdatedProductData(previousProductsData, productId, timeSlotId, key, value),
      );

      // The produced quantity has no impact on the recipes and ingredients needs
      if (key === 'produced') {
        return;
      }

      await refreshRecipesAndIngredients();
    } catch {
      showErrorMessage(i18next.t('PRODUCTION.PLANNING.SAVE_ERROR'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecipeStockChange = async (recipeId, timeSlotId, inStock) => {
    const row = recipesData.rows.find((recipe) => recipe.originalRecipeId === recipeId);

    /**
     * inStock can be input in either the recipe's base unit or batch production unit
     * while it is stored in db in the recipe's base unit
     * This ensures a correct inStock value
     */

    const inStockInBaseUnit =
      inStock !== null
        ? recipeUnitSelections[recipeId] === RECIPE_UNIT_TYPE.BATCH_PRODUCTION
          ? inStock * row.unitQuantity
          : convertInpulseUnitToEntityQuantity(inStock, row.unit)
        : null;

    // The chk_entity_in_stock DB constraint only accepts up to 2 decimals for recipe stocks:
    // unit conversion and batch multiplication can add extra decimals or floating point noise
    const roundedInStockInBaseUnit =
      inStockInBaseUnit !== null ? roundWithDecimals(inStockInBaseUnit, 2) : null;

    // The stored stock is in the recipe's base unit, so it is compared to the converted input value
    const hasValueChanged =
      !row.dataByTimeSlotIds[timeSlotId] ||
      row.dataByTimeSlotIds[timeSlotId].inStock !== roundedInStockInBaseUnit;

    if (!hasValueChanged) {
      return;
    }

    await handleSaveRecipe(recipeId, timeSlotId, roundedInStockInBaseUnit);
  };

  const handleSaveRecipe = async (recipeId, timeSlotId, inStock) => {
    setIsLoading(true);

    try {
      await productionPlanningService.postRecipeProductionEntry(
        selectedStore.id,
        moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        recipeId,
        timeSlotId,
        inStock,
      );

      completedSavesCountRef.current += 1;

      await refreshRecipesAndIngredients();
    } catch {
      showErrorMessage(i18next.t('PRODUCTION.PLANNING.SAVE_ERROR'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetProductionEntries = async ({ productIds }) => {
    setIsLoading(true);

    try {
      await productionPlanningService.resetProductionEntries(
        selectedStore.id,
        productIds,
        moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        true, // isOperatorForecastReset
      );

      // Refresh production entries in order to get correct production entries values
      await refetchAfterAction();

      setSelectedRows([]);
      selectedRowsRef.current = [];
      setRowSelectionModel([]);
    } catch {
      showErrorMessage(i18next.t('FORECAST.POS_PRODUCTION_PLANNING.ERROR_RESETING_DATA'));
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedProductsOrAllProducts = (selectedRowsData) => {
    let concernedProducts = productsData.rows;

    if (selectedRowsData.length) {
      // getSelectedRows returns a Map, so we need to convert it to an array
      concernedProducts = Array.from(selectedRows.values());
    }

    return concernedProducts;
  };

  const handleProductsSelection = (rowIds) => {
    // Need to get the rows data from the row ids because row.id contains the dataKey (cf 'getProductionEntries')
    const selectedIds = new Set(rowIds);
    const selectedRowsData = productsData.rows.filter((row) => selectedIds.has(row.id));

    setSelectedRows(selectedRowsData);
    selectedRowsRef.current = selectedRowsData;
  };

  /*******************
   ***** OTHER *****
   *******************/

  const customToolbar = () => (
    <ProductionPlanningDatagridToolbar
      handleDateChange={handleDateChange}
      isDisabled={isLoading}
      maxFutureDate={moment(TODAY).add(prevHorizon.future, 'days')}
      selectedDate={selectedDate}
    />
  );

  return (
    <GenericPageContainer>
      <NavigationBreadCrumb featurePath={match.path} />
      <GenericContentContainer height="calc(100% - 40px)" overflow="hidden">
        <Box sx={{ paddingBottom: '24px' }}>
          <LeftRightSplitter
            left={
              <GenericGapContainer>
                <Dropdown
                  iconSrc="/images/inpulse/pin-black-small.svg"
                  isDisabled={isLoading}
                  items={stores}
                  searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                  selectedItem={selectedStore}
                  isRequired
                  onSelectionChange={setSelectedStore}
                />
              </GenericGapContainer>
            }
            right={
              <Button
                actions={actions}
                color={'inpulse-outline'}
                iconDropdown={'/images/inpulse/carret-black.svg'}
                iconOnLeft={false}
                label={i18next.t('GENERAL.ACTIONS')}
              />
            }
          />
        </Box>

        <Box sx={{ borderColor: 'divider' }}>
          <Tabs aria-label="production planning tabs" value={tabValue} onChange={handleTabChange}>
            <Tab label={i18next.t('GENERAL.PRODUCT_PLURAL')} />
            <Tab label={i18next.t('GENERAL.RECIPE_PLURAL')} />
            <Tab label={i18next.t('GENERAL.INGREDIENT_PLURAL')} />
          </Tabs>
        </Box>
        <TabPanel index={0} value={tabValue}>
          <StyledDataGridContainer
            data-first-forecast-focused={isFirstForecastFocused()}
            data-has-time-slots={timeSlots.length > 1}
          >
            <DataGridPro
              apiRef={apiRef}
              columns={productsColumns}
              hideFooter={false}
              loading={isLoading}
              localeText={
                DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid
                  .defaultProps.localeText
              }
              pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
              paginationModel={productsPaginationModel}
              rowHeight={60}
              rows={productsData.rows || []}
              slots={{
                ...DATAGRID_SLOTS,
                toolbar: customToolbar,
                baseCheckbox: StyledCheckbox,
                loadingOverlay: DataGridLoadingOverlay,
              }}
              pagination
              {...COMMON_DATAGRID_PROPS}
              disableColumnResize={false}
              rowSelectionModel={rowSelectionModel}
              checkboxSelection
              checkboxSelectionVisibleOnly
              disableVirtualization
              onPaginationModelChange={setProductsPaginationModel}
              onRowSelectionModelChange={(rowIds) => {
                setRowSelectionModel(rowIds);
                handleProductsSelection(rowIds);
              }}
            />
          </StyledDataGridContainer>
        </TabPanel>
        <TabPanel index={1} value={tabValue}>
          <StyledDataGridContainer>
            <DataGridPro
              apiRef={apiRef}
              columns={recipesColumns}
              hideFooter={false}
              loading={isLoading}
              localeText={
                DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid
                  .defaultProps.localeText
              }
              pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
              paginationModel={recipesPaginationModel}
              rowHeight={60}
              rows={recipesData.rows || []}
              slots={{
                ...DATAGRID_SLOTS,
                toolbar: customToolbar,
                loadingOverlay: DataGridLoadingOverlay,
              }}
              autoHeight
              pagination
              {...COMMON_DATAGRID_PROPS}
              disableColumnResize={false}
              initialState={{
                ...COMMON_DATAGRID_PROPS.initialState,
                pinnedColumns: {
                  left: ['__check__', 'name', 'category', 'unit'],
                  right: ['total'],
                },
              }}
              onPaginationModelChange={setRecipesPaginationModel}
            />
          </StyledDataGridContainer>
        </TabPanel>
        <TabPanel index={2} value={tabValue}>
          <StyledDataGridContainer>
            <DataGridPro
              apiRef={apiRef}
              columns={ingredientsColumns}
              hideFooter={false}
              loading={isLoading}
              localeText={
                DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid
                  .defaultProps.localeText
              }
              pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
              paginationModel={ingredientsPaginationModel}
              rowHeight={60}
              rows={ingredientsData || []}
              slots={{
                ...DATAGRID_SLOTS,
                toolbar: customToolbar,
                loadingOverlay: DataGridLoadingOverlay,
              }}
              autoHeight
              pagination
              {...COMMON_DATAGRID_PROPS}
              disableColumnResize={false}
              onPaginationModelChange={setIngredientsPaginationModel}
            />
          </StyledDataGridContainer>
        </TabPanel>
      </GenericContentContainer>
    </GenericPageContainer>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  prevHorizon: getPrevHorizon(state.baseReducer.userRights),
  stores: getSalesPointStores(state.baseReducer.activeStores),
  user: state.baseReducer.user,
  currency: state.baseReducer.currency,
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/operations/production/planning',
  ),
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  showMessage: (message, type) => {
    dispatch(showMessage(message, type));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductionPlanning);
